import React, { useEffect, useState } from "react";
import Layout, { PageHeader, Surface } from "@/components/Layout";
import { api } from "@/lib/api";

const methodColor = {
  GET: "bg-blue-50 text-blue-700 border-blue-100",
  POST: "bg-emerald-50 text-emerald-700 border-emerald-100",
  PATCH: "bg-amber-50 text-amber-700 border-amber-100",
  DELETE: "bg-red-50 text-red-700 border-red-100",
};

export default function AuditLogs() {
  const [items, setItems] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [merchantId, setMerchantId] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get("/admin/merchants").then((r) => setMerchants(r.data.items)); }, []);
  useEffect(() => {
    const q = merchantId ? `?merchant_id=${merchantId}&limit=300` : "?limit=300";
    api.get(`/admin/audit-logs${q}`).then((r) => setItems(r.data.items));
  }, [merchantId]);

  return (
    <Layout>
      <div className="p-6 lg:p-10" data-testid="audit-logs-page">
        <PageHeader title="Audit Logs" subtitle="Every privileged action and proxy call. Append-only." />

        <Surface className="p-3 mb-4 flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">Merchant</span>
          <select
            data-testid="audit-merchant-filter"
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
            className="h-8 text-xs px-2 border border-zinc-200 rounded-sm bg-white focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none"
          >
            <option value="">All scopes</option>
            {merchants.map(m => <option key={m.id} value={m.id}>{m.company_name}</option>)}
          </select>
          <div className="ml-auto text-xs text-zinc-500">{items.length} entries</div>
        </Surface>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Surface className="lg:col-span-2 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                  <th className="px-5 py-3">When</th>
                  <th className="px-5 py-3">Action</th>
                  <th className="px-5 py-3">Method</th>
                  <th className="px-5 py-3">Path</th>
                  <th className="px-5 py-3">Actor</th>
                  <th className="px-5 py-3">IP</th>
                  <th className="px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => setSelected(a)}
                    data-testid={`audit-row-${a.id}`}
                    className={`border-b border-zinc-50 cursor-pointer transition-colors duration-150 ${selected?.id === a.id ? "bg-zinc-100" : "hover:bg-zinc-50/60"}`}
                  >
                    <td className="px-5 py-3 text-xs font-mono text-zinc-500">{(a.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-5 py-3 font-mono text-xs">{a.action}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] font-mono uppercase tracking-[0.05em] px-1.5 py-0.5 rounded-sm border ${methodColor[a.method] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>{a.method}</span>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-zinc-600">{a.path}</td>
                    <td className="px-5 py-3 text-xs">{a.actor_type}</td>
                    <td className="px-5 py-3 font-mono text-xs">{a.ip_address || "—"}</td>
                    <td className="px-5 py-3 font-mono text-xs">{a.status_code}</td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-xs text-zinc-500">No audit entries.</td></tr>}
              </tbody>
            </table>
          </Surface>

          <Surface className="p-5 lg:sticky lg:top-6 self-start">
            <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-2">Inspector</div>
            {!selected ? (
              <p className="text-xs text-zinc-500">Select an audit entry to inspect request &amp; response payloads.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">Request</div>
                  <pre className="mt-2 text-[11px] font-mono bg-zinc-950 text-emerald-200 p-3 rounded-sm overflow-x-auto max-h-56">
{JSON.stringify(selected.request_data || {}, null, 2)}
                  </pre>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">Response</div>
                  <pre className="mt-2 text-[11px] font-mono bg-zinc-950 text-blue-200 p-3 rounded-sm overflow-x-auto max-h-56">
{JSON.stringify(selected.response_data || {}, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </Surface>
        </div>
      </div>
    </Layout>
  );
}
